import { AdvertiseStepper } from './AdvertiseStepper';
import { Logo } from '@/components/common/Logo';
import { useBeforeUnload } from '@/hooks/useBeforeUnload';
import { useAdvertise } from '@/store/useAdvertise';

const SKIP_DIRTY_KEYS = new Set(['consent', 'contactPref']);

function trackHasMeaningfulData(track) {
  if (!track) return false;
  return Object.entries(track).some(([key, value]) => {
    if (SKIP_DIRTY_KEYS.has(key)) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'boolean') return false;
    return value !== '' && value != null;
  });
}

// Shared chrome for every step page across the 3 advertise tracks: a slim
// header (logo + stepper), a two-column body (form + side summary), and a
// slot for the shared footer nav (Back / Continue).
export function AdvertiseLayout({ trackLabel, steps, currentStepId, title, subtitle, sidebar, footer, children }) {
  const agents = useAdvertise((s) => s.agents);
  const sellers = useAdvertise((s) => s.sellers);
  const builders = useAdvertise((s) => s.builders);
  useBeforeUnload(
    trackHasMeaningfulData(agents) || trackHasMeaningfulData(sellers) || trackHasMeaningfulData(builders)
  );

  const currentIndex = Math.max(0, steps.findIndex((s) => s.id === currentStepId));
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-5xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Logo size="sm" />
            <span className="text-xs font-semibold uppercase tracking-wide text-blue-600">{trackLabel}</span>
          </div>
          <div className="mt-4"><AdvertiseStepper steps={steps} currentIndex={currentIndex} /></div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
          <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <h1 className="font-display text-2xl font-bold text-slate-900 sm:text-3xl">{title}</h1>
            {subtitle && <p className="mt-2 text-slate-600">{subtitle}</p>}
            <p className="mt-2 text-xs text-slate-400">Your progress is saved in this browser tab until you close it.</p>
            <div className="mt-6">{children}</div>
            {footer}
          </div>
          {sidebar && <div>{sidebar}</div>}
        </div>
      </div>
    </div>
  );
}
